import { type ReactNode, useCallback } from 'react';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import type { UseFormReturn } from 'react-hook-form';
// eslint-disable-next-line import/no-cycle
import { useTranslation } from 'react-i18next';

import { FormItem } from 'components/shared';
import { TextFieldElement } from 'components/muiInputWrappers';
import { validationRules } from 'helpers/utils';

import ActionButton from './ActionButton';

type Props = {
  form: UseFormReturn<{
    text: string;
  }>;
  handleClearSearchValue: () => void;
  onSubmit: (data: { text: string }) => void;
  childrenActionButton?: ReactNode;
  childrenSelectOptions?: ReactNode;
  hasOnEnter?: boolean;
  disabled?: boolean;
  havePlaceholder?: boolean;
  hideSearchIcon?: boolean;
  placeholderText?: string;
};

function SearchInput({
  form,
  onSubmit,
  handleClearSearchValue,
  childrenActionButton = null,
  childrenSelectOptions = null,
  hasOnEnter = true,
  disabled = false,
  havePlaceholder = false,
  placeholderText,
  hideSearchIcon = false
}: Props) {
  const searchValue = form.watch('text');
  const { t } = useTranslation('global');
  const { validateSpaces } = validationRules();

  const onEnter = useCallback(
    ({ key }) => {
      if (key === 'Enter') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        form.handleSubmit(onSubmit)();
      }
    },
    [form, onSubmit]
  );

  return (
    <form className="searchInput" onSubmit={form.handleSubmit(onSubmit)}>
      {childrenSelectOptions && childrenSelectOptions}

      <FormItem
        data-testid="catalog-layout-search-input"
        style={{
          maxWidth: childrenSelectOptions ? '32em' : '600px',
          width: '100%'
        }}
      >
        <TextFieldElement
          control={form.control}
          disabled={disabled}
          InputProps={{
            endAdornment: (
              <>
                {searchValue && (
                  <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="center"
                    padding="4px"
                    sx={{
                      borderRadius: '50%',
                      '&:hover': {
                        cursor: 'pointer',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <CloseIcon
                      fontSize="small"
                      onClick={handleClearSearchValue}
                      sx={{
                        color: 'rgba(0, 0, 0, 0.54)'
                      }}
                      titleAccess="Clear"
                    />
                  </Box>
                )}
                {hideSearchIcon ? null : (
                  <SearchIcon
                    onClick={form.handleSubmit(onSubmit)}
                    sx={{
                      '&:hover': {
                        cursor: 'pointer'
                      }
                    }}
                    type="submit"
                  />
                )}
              </>
            )
          }}
          name="text"
          onKeyUp={hasOnEnter ? onEnter : null}
          placeholder={havePlaceholder ? placeholderText : ''}
          size="small"
          type="text"
          validation={{
            validate: validateSpaces
          }}
          variant="outlined"
          fullWidth
        />
      </FormItem>
      {childrenActionButton && (
        <ActionButton>{childrenActionButton}</ActionButton>
      )}
    </form>
  );
}

export default SearchInput;
